import React, { useRef } from 'react';
import { Badge, Button, Modal, Table } from 'react-bootstrap';
import propTypes from 'prop-types';
import * as XLSX from 'xlsx';
import './styles.scss';

const ValidationErrorsModal = ({ data, show, handleClose }) => {
    const handleModalClose = () => {
        handleClose();
    }

    const issueCount = data?.length || 0;
    const hasMultiplePages = issueCount > 8; // Consider anything more than 8 rows as needing scroll

    const tableContainerRef = useRef(null);

    const exportToExcel = () => {
        const workbook = XLSX.utils.book_new();
        const excelData = data.map((item, index) => ({
            "Issue #": index + 1,
            "Sheet Name": item.sheetName,
            "Data Point": item.dataPointName,
            "Column(s)": item.column,
            "Row(s)": item.row,
            "What is expected": item.errorMessage
        }));
        
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Survey Subission Issues");
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-").substring(0, 19);
        XLSX.writeFile(workbook, `submission-issues-${timestamp}.xlsx`);
    };

    if (!data) {
        return null;
    }

    return (
        <Modal
            className="validation-errors-modal"
            show={show}
            onHide={handleModalClose}
            centered
            size="xl"
            aria-labelledby="validation-errors-modal-title"
        >
            <Modal.Header closeButton>
                <Modal.Title id="validation-errors-modal-title">
                    Data Issues to Resolve  <Badge bg="danger">{issueCount} {issueCount === 1 ? 'issue' : 'issues'}</Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="explanation-text">
                    <p>
                        The following <strong>{issueCount}</strong> {issueCount === 1 ? 'issue was' : 'issues were'} found in your uploaded file submission.
                        Please correct {issueCount === 1 ? 'this item' : 'these items'} in your file and upload again.
                        Each issue shows the affected spreadsheet sheet name, data point, location, and what needs to be fixed.
                    </p>
                    {hasMultiplePages && (
                        <p className="scroll-hint">
                            <i className="fa fa-arrow-down" aria-hidden="true"></i> Scroll down to see all {issueCount} issues
                        </p>
                    )}
                </div>
                
                <div className="table-actions">
                    <Button 
                        className="export-button"
                        onClick={exportToExcel}
                        aria-label={`Export ${issueCount} issues to Excel file`}
                    >
                        <i className="fa fa-file-excel" aria-hidden="true"></i> Export to Excel
                    </Button>
                </div>
                
                <div
                    ref={tableContainerRef}
                    className={`validation-errors-table ${hasMultiplePages ? 'has-more-issues' : ''}`}
                >
                    <Table striped bordered hover>
                        <caption className="sr-only">{issueCount} data issues found during file upload</caption>
                        <thead>
                            <tr>
                                <th scope="col">Sheet Name</th>
                                <th scope="col">Data Point</th>
                                <th scope="col">Column(s)</th>
                                <th scope="col">Row(s)</th>
                                <th scope="col">What is expected</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, index) => (
                                <tr key={`${item.sheetName}-${item.row}-${item.column}-${index}`}>
                                    <td>{item.sheetName}</td>
                                    <td>{item.dataPointName}</td>
                                    <td>{item.column}</td>
                                    <td>{item.row}</td>
                                    <td aria-live="polite" className="error-message">{item.errorMessage}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="issue-count-summary">
                    <span className="issues-remaining">
                        {issueCount} {issueCount === 1 ? 'issue' : 'issues'} to resolve
                    </span>
                    {hasMultiplePages && (
                        <span className="scroll-reminder"> (scroll to view all)</span>
                    )}
                </div>
                <Button
                    className='btn btn-secondary'
                    onClick={handleModalClose}
                    aria-label="Close validation errors and return to previous screen"
                >
                    Go Back
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

ValidationErrorsModal.propTypes = {
    data: propTypes.array,
    show: propTypes.bool,
    handleClose: propTypes.func
}

export default ValidationErrorsModal;